import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchInvoices, generateInvoice } from '../../api/invoices';
import { downloadAmazonOrderDocument, fetchOrder } from '../../api/orders';
import {
  Badge,
  EmptyState,
  PageHeader,
  StatusBanner,
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';

function InvoicesPage({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [orderId, setOrderId] = useState('');
  const [lookedUpOrder, setLookedUpOrder] = useState(null);
  const [status, setStatus] = useState({ type: 'loading', message: '' });
  const [generating, setGenerating] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [busyDoc, setBusyDoc] = useState('');

  const loadInvoices = useCallback(async () => {
    setStatus({ type: 'loading', message: '' });

    try {
      const payload = await fetchInvoices(token);
      setInvoices(payload.invoices || []);
      setStatus({ type: 'ready', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  }, [token]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleLookup = async (event) => {
    event.preventDefault();
    const reference = orderId.trim();
    if (!reference) {
      setStatus({ type: 'error', message: 'Enter an Amazon order ID.' });
      return;
    }

    setLookingUp(true);
    setLookedUpOrder(null);
    setStatus({ type: 'loading', message: '' });

    try {
      const payload = await fetchOrder(token, reference);
      const order = payload.order;
      if (!order?.id) {
        throw new Error('Order not found. Sync it from Unshipped orders first.');
      }
      setLookedUpOrder(order);
      setStatus({
        type: 'success',
        message: `Found ${order.amazonOrderId}. Download Amazon PDFs below, or generate a CRM invoice.`,
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Order not found. Sync the order from Amazon first.',
      });
    } finally {
      setLookingUp(false);
    }
  };

  const handleGenerate = async () => {
    const reference = lookedUpOrder?.id || orderId.trim();
    if (!reference) {
      setStatus({ type: 'error', message: 'Enter an order id.' });
      return;
    }

    setGenerating(true);

    try {
      await generateInvoice(token, reference);
      setStatus({ type: 'success', message: 'CRM invoice generated from order.' });
      await loadInvoices();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleAmazonDocument = async (documentType, targetRef) => {
    const reference = (targetRef || lookedUpOrder?.amazonOrderId || lookedUpOrder?.id || orderId).trim();
    if (!reference) {
      setStatus({ type: 'error', message: 'Enter an Amazon order ID.' });
      return;
    }

    setBusyDoc(documentType);
    const docLabel =
      documentType === 'shipping_label'
        ? 'shipping label'
        : documentType === 'packing_slip'
          ? 'packing slip'
          : 'tax invoice';

    setStatus({
      type: 'loading',
      message: `Fetching Amazon ${docLabel} for ${reference}…`,
    });

    try {
      const payload = await downloadAmazonOrderDocument(token, reference, documentType);
      setStatus({
        type: 'success',
        message: `Downloaded ${payload.filename || `${docLabel}.pdf`}.`,
      });

      // If order card not visible yet, load order details in background
      if (!lookedUpOrder) {
        try {
          const orderPayload = await fetchOrder(token, reference);
          if (orderPayload?.order?.id) {
            setLookedUpOrder(orderPayload.order);
          }
        } catch {
          // ignore lookup failure if PDF already downloaded
        }
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to download Amazon document.' });
    } finally {
      setBusyDoc('');
    }
  };

  return (
    <section className="grid gap-5">
      <PageHeader
        title="Invoices"
        subtitle="Paste an Amazon order ID to download packing slip / tax invoice / shipping label, or generate a CRM invoice."
      />

      <StatusBanner status={status} setStatus={setStatus} />

      <form className={`${panelClassName} grid gap-4 p-5 md:max-w-2xl`} onSubmit={handleLookup}>
        <h3 className="text-base font-semibold text-slate-900">Find order documents</h3>
        <p className="text-sm text-slate-600">
          Same Amazon PDFs as Seller Central: packing slip, tax invoice, and shipping label (when
          available).
        </p>
        <label className="grid gap-1.5">
          <span className={labelClassName}>Amazon order ID</span>
          <input
            className={inputClassName}
            value={orderId}
            onChange={(event) => {
              setOrderId(event.target.value);
              setLookedUpOrder(null);
            }}
            placeholder="405-7651461-0179517"
          />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={primaryButtonClassName}
            onClick={() => handleAmazonDocument('shipping_label')}
            disabled={!!busyDoc || lookingUp}
          >
            {busyDoc === 'shipping_label' ? 'Downloading label…' : 'Download shipping label'}
          </button>
          <button
            type="submit"
            className={secondaryButtonClassName}
            disabled={lookingUp || !!busyDoc}
          >
            {lookingUp ? 'Finding…' : 'Find order'}
          </button>
        </div>
      </form>

      {lookedUpOrder ? (
        <div className={`${panelClassName} grid gap-4 p-5 md:max-w-2xl`}>
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {lookedUpOrder.amazonOrderId}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {lookedUpOrder.buyerName || 'Buyer —'} · {lookedUpOrder.marketplace} ·{' '}
              {lookedUpOrder.status || '—'}
            </p>
            {lookedUpOrder.items?.[0]?.title ? (
              <p className="mt-2 line-clamp-2 text-sm text-slate-700">
                {lookedUpOrder.items[0].title}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={() => handleAmazonDocument('shipping_label')}
              disabled={!!busyDoc}
            >
              {busyDoc === 'shipping_label' ? 'Downloading…' : 'Download shipping label'}
            </button>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => handleAmazonDocument('packing_slip')}
              disabled={!!busyDoc}
            >
              {busyDoc === 'packing_slip' ? 'Downloading…' : 'Print packing slip'}
            </button>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => handleAmazonDocument('tax_invoice')}
              disabled={!!busyDoc}
            >
              {busyDoc === 'tax_invoice' ? 'Downloading…' : 'Print tax invoice'}
            </button>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <p className="mb-3 text-sm text-slate-600">
              Optional: create a CRM invoice record (printable HTML in this app).
            </p>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={handleGenerate}
              disabled={generating || !!busyDoc}
            >
              {generating ? 'Generating…' : 'Generate CRM invoice'}
            </button>
          </div>
        </div>
      ) : null}

      {status.type === 'loading' && !invoices.length && !lookedUpOrder ? (
        <p className="text-sm text-slate-500">Loading invoices...</p>
      ) : invoices.length ? (
        <div className={`${panelClassName} overflow-x-auto`}>
          <table className="w-full min-w-[40rem] border-collapse">
            <thead>
              <tr>
                <th className={tableHeadClassName}>Invoice #</th>
                <th className={tableHeadClassName}>Order</th>
                <th className={tableHeadClassName}>Amount</th>
                <th className={tableHeadClassName}>Status</th>
                <th className={tableHeadClassName}>Issued</th>
                <th className={tableHeadClassName}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className={tableCellClassName}>{invoice.invoiceNumber}</td>
                  <td className={tableCellClassName}>
                    {invoice.order?.amazonOrderId || invoice.orderId || '—'}
                  </td>
                  <td className={tableCellClassName}>{Number(invoice.amount).toFixed(2)}</td>
                  <td className={tableCellClassName}>
                    <Badge tone={invoice.status === 'issued' ? 'success' : 'warning'}>
                      {invoice.status}
                    </Badge>
                  </td>
                  <td className={tableCellClassName}>
                    {new Date(invoice.issuedAt).toLocaleDateString()}
                  </td>
                  <td className={tableCellClassName}>
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/admin/invoices/${invoice.id}`}
                        className="font-semibold text-blue-700 hover:text-blue-800"
                      >
                        View / Print
                      </Link>
                      {(invoice.order?.amazonOrderId || invoice.orderId) && (
                        <button
                          type="button"
                          onClick={() =>
                            handleAmazonDocument(
                              'shipping_label',
                              invoice.order?.amazonOrderId || invoice.orderId
                            )
                          }
                          className="text-xs font-medium text-slate-600 hover:text-slate-900 underline"
                          disabled={!!busyDoc}
                        >
                          Shipping Label
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="No CRM invoices yet"
          description="Find an order above to download Amazon PDFs, or generate a CRM invoice record."
        />
      )}
    </section>
  );
}

export default InvoicesPage;
